import Swiper from 'swiper';
import { Navigation, Pagination } from 'swiper/modules';

import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';

/*
	Shared Shopify Buy Button config. Per voucher, only the product ID
	changes (data-shopify-product-id on the mount). The storefront token
	is the public one from the Buy Button embed, not an Admin API secret.
*/
const SHOPIFY_SCRIPT_URL = 'https://sdks.shopifycdn.com/buy-button/latest/buy-button-storefront.min.js';

// Same file the site buttons use. Shopify's own loader requests the legacy
// Google Fonts API, which serves a different Sora cut at the same weight.
const SORA_STYLESHEET = 'https://fonts.googleapis.com/css2?family=Sora:wght@300;400;500;600;700&display=block';

const SHOPIFY = {
	domain: 'the-vsb-bathhouse.myshopify.com',
	storefrontAccessToken: '1e7d3ef38e106455678f6b4de5dd1574',
	moneyFormat: '€{{amount}}',
};

let shopifyUiPromise = null;

function buyButtonOptions () {

	return {
		product: {
			styles: {
				product: {
					'@media (min-width: 601px)': {
						'max-width': '100%',
						'margin-left': '0px',
						'margin-bottom': '0px',
					},
				},
				button: {
					'font-family': 'Sora, sans-serif',
					'font-weight': '500',
					'font-size': '14px',
					'line-height': 'calc(18 / 14)',
					'letter-spacing': '0.15em',
					'text-transform': 'uppercase',
					// Shopify's iframe reset sets antialiased/grayscale on html,
					// which draws the same Sora thinner than the site buttons.
					'-webkit-font-smoothing': 'auto',
					'-moz-osx-font-smoothing': 'auto',
					'text-rendering': 'auto',
					'color': '#ffffff',
					'background-color': '#013B49',
					'border-radius': '999px',
					'padding-top': '18px',
					'padding-bottom': '18px',
					'padding-left': '40px',
					'padding-right': '40px',
					'width': '100%',
					'display': 'inline-flex',
					'align-items': 'center',
					'justify-content': 'center',
					'svg': {
						'display': 'inline-block',
						'width': '20px',
						'height': '20px',
						'margin-left': '10px',
						'flex-shrink': '0',
					},
					':hover': {
						'background-color': '#02647c',
					},
					':focus': {
						'background-color': '#02647c',
					},
				},
			},
			buttonDestination: 'checkout',
			contents: {
				img: false,
				imgWithCarousel: false,
				title: false,
				variantTitle: false,
				price: false,
				unitPrice: false,
				options: false,
				quantity: false,
				quantityIncrement: false,
				quantityDecrement: false,
				quantityInput: false,
				button: true,
				buttonWithQuantity: false,
				description: false,
			},
			width: '100%',
			text: {
				button: 'Buy Physical Gift\u00A0Voucher',
			},
		},
		// Default is a separate checkout window. False navigates this tab.
		cart: {
			popup: false,
		},
	};

}

function whenShopifyReady () {

	if (window.ShopifyBuy && window.ShopifyBuy.UI) {
		return Promise.resolve ();
	}

	return new Promise ((resolve, reject) => {

		const fail = () => reject (new Error ('Shopify Buy Button failed to load'));

		const onload = () => {
			if (window.ShopifyBuy && window.ShopifyBuy.UI) {
				resolve ();
			} else {
				fail ();
			}
		};

		const existing = document.querySelector ('script[data-shopify-buy-button]');

		if (existing) {
			existing.addEventListener ('load', onload);
			existing.addEventListener ('error', fail);
			return;
		}

		const script = document.createElement ('script');
		script.async = true;
		script.src = SHOPIFY_SCRIPT_URL;
		script.dataset.shopifyBuyButton = '';
		script.onload = onload;
		script.onerror = fail;
		(document.head || document.body).appendChild (script);

	});

}

function shopifyUi () {

	if (! shopifyUiPromise) {
		shopifyUiPromise = whenShopifyReady ().then (() => {
			const client = window.ShopifyBuy.buildClient ({
				domain: SHOPIFY.domain,
				storefrontAccessToken: SHOPIFY.storefrontAccessToken,
			});
			return window.ShopifyBuy.UI.onReady (client);
		});
	}

	return shopifyUiPromise;

}

const EXTERNAL_LINK_ICON = '<svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false"><path d="M9.16667 3.33333H3.33333V15C3.33333 15.9205 4.07952 16.6667 5 16.6667H15C15.9205 16.6667 16.6667 15.9205 16.6667 15V10.8333" stroke="currentColor" stroke-width="1.25" stroke-linecap="round" stroke-linejoin="round"/><path d="M7.5 12.5L16.6667 3.33333" stroke="currentColor" stroke-width="1.25" stroke-linecap="round" stroke-linejoin="round"/><path d="M12.5 3.33333H16.6667V7.5" stroke="currentColor" stroke-width="1.25" stroke-linecap="round" stroke-linejoin="round"/></svg>';

function openCheckoutInNewTab () {

	const nativeOpen = window.open.bind (window);
	let restored = false;

	const restore = () => {
		if (restored) return;
		restored = true;
		window.open = nativeOpen;
	};

	window.open = function (url, name, features) {

		restore ();

		if (name !== 'checkout') {
			return nativeOpen (url, name, features);
		}

		const opened = nativeOpen ('', '_blank');

		if (opened) opened.opener = null;

		return opened;

	};

	window.setTimeout (restore, 10000);

}

function decorateBuyButton (iframe) {

	const doc = iframe.contentDocument;
	const button = doc && doc.querySelector ('button.shopify-buy__btn');

	if (! button || button.dataset.voyaDecorated) return;

	button.dataset.voyaDecorated = '1';
	button.insertAdjacentHTML ('beforeend', EXTERNAL_LINK_ICON);
	button.addEventListener ('click', openCheckoutInNewTab, true);

}

function loadSiteSora (node) {

	const inject = (iframe) => {

		const doc = iframe.contentDocument;

		if (! doc || ! doc.head || doc.getElementById ('voya-sora')) return;

		const link = doc.createElement ('link');
		link.id = 'voya-sora';
		link.rel = 'stylesheet';
		link.href = SORA_STYLESHEET;
		doc.head.appendChild (link);

	};

	const watch = (iframe) => {

		if (iframe.contentDocument && iframe.contentDocument.readyState === 'complete') {
			inject (iframe);
			decorateBuyButton (iframe);
		}

		iframe.addEventListener ('load', () => {
			inject (iframe);
			decorateBuyButton (iframe);
		});

		const watchButton = () => {
			const body = iframe.contentDocument && iframe.contentDocument.body;
			if (! body) return;
			decorateBuyButton (iframe);
			const observer = new MutationObserver (() => decorateBuyButton (iframe));
			observer.observe (body, { childList: true, subtree: true });
		};

		if (iframe.contentDocument && iframe.contentDocument.body) {
			watchButton ();
		} else {
			iframe.addEventListener ('load', watchButton);
		}

	};

	const existing = node.querySelector ('iframe');

	if (existing) {
		watch (existing);
		return;
	}

	const observer = new MutationObserver (() => {

		const iframe = node.querySelector ('iframe');

		if (! iframe) return;

		watch (iframe);
		observer.disconnect ();

	});

	observer.observe (node, { childList: true, subtree: true });

}

function mountShopifyBuyButtons () {

	const mounts = Array.from (document.querySelectorAll (
		'section.listing-vouchers .shopify-buy[data-shopify-product-id]:not([data-shopify-ready])'
	));

	if (! mounts.length) return;

	mounts.forEach ((node) => {
		node.setAttribute ('data-shopify-ready', '');
	});

	shopifyUi ().then ((ui) => {

		mounts.forEach ((node) => {
			loadSiteSora (node);
			ui.createComponent ('product', {
				id: node.dataset.shopifyProductId,
				node,
				moneyFormat: SHOPIFY.moneyFormat,
				options: buyButtonOptions (),
			});
		});

	}).catch (() => {

		shopifyUiPromise = null;
		mounts.forEach ((node) => {
			node.removeAttribute ('data-shopify-ready');
		});

	});

}

export default function listingVouchers () {

	// Independent of the swiper early-return below, so a preview refresh
	// that finds no new swipers still mounts any new Buy buttons.
	mountShopifyBuyButtons ();

	// Skip containers already initialised (eg. re-running after an ACF
	// block preview refresh in the editor - see hook below)
	const swipers = document.querySelectorAll('section.listing-vouchers .swiper:not(.swiper-initialized)');

	if (! swipers.length) return;



	// Init swiper
	swipers.forEach(function (container) {

		const section = container.closest('section');
		const group = container.closest('.group');

		new Swiper (container, {
			modules: [Navigation, Pagination],
			loop: false,
			spaceBetween: 24,
			breakpoints: {
				0:   { slidesPerView: 1 },
				768: { slidesPerView: 2 },
				1024: { slidesPerView: 3 },
			},
			pagination: {
				el: group.querySelector('.swiper-pagination'),
				type: 'progressbar',
			},
			navigation: {
				prevEl: group.querySelector('.swipe-prev'),
				nextEl: group.querySelector('.swipe-next'),
			},
		});

	});

}



/*
	ACF Blocks v3 re-fetches and re-renders this block's preview markup
	inside the editor (eg. on attribute change) without a page reload, so
	'DOMContentLoaded' only covers the first render - re-run init on every
	preview refresh too. ACF's block-preview JS may run in the iframe's own
	window or in the parent admin window depending on context, so check both.
*/
const acfInstance = (typeof window !== 'undefined')
	? (window.acf || (window.parent !== window && window.parent.acf))
	: null
;

if (acfInstance) {
	acfInstance.addAction('render_block_preview/type=listing-vouchers', listingVouchers);
}
